/**
 * ================================================================
 * SESSION SERVICE
 * ================================================================
 *
 * Manages local session persistence and state.
 * Handles storage of tokens, "Remember Me" preferences, and saved accounts.
 *
 * @module features/auth/services
 */

import { Preferences } from "@capacitor/preferences";
import { type UserRole } from "@/stores/authStore";

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: "access_token",
  REMEMBER_ME: "mebi_remember_login",
  SAVED_ACCOUNTS: "mebi_saved_accounts",
  SELECTED_ACCOUNT: "mebi_selected_account",
} as const;

export interface SavedAccount {
  id: string;
  email: string;
  phone?: string;
  fullName: string;
  avatarUrl?: string;
  nickname?: string;
  organizerName?: string;
  organizerAvatarUrl?: string;
  lastLoginAt: string;
  rememberMe?: boolean;
  roles: UserRole[];
  expiresAt?: string;
}

export const sessionService = {
  /**
   * Persists access and refresh tokens to secure storage.
   */
  async storeTokens(accessToken: string): Promise<void> {
    await Preferences.set({
      key: STORAGE_KEYS.ACCESS_TOKEN,
      value: accessToken,
    });
  },

  /**
   * Get stored access token
   */
  async getAccessToken(): Promise<string | null> {
    const { value } = await Preferences.get({ key: STORAGE_KEYS.ACCESS_TOKEN });
    return value;
  },

  /**
   * Clear tokens on logout
   */
  async clearTokens(): Promise<void> {
    await Preferences.remove({ key: STORAGE_KEYS.ACCESS_TOKEN });
  },

  /**
   * Clear session (alias for clearTokens for backward compatibility)
   */
  async clearSession(): Promise<void> {
    await this.clearTokens();

    // Clear legacy LocalStorage keys that might not be handled by Capacitor Preferences
    if (globalThis.window?.localStorage) {
      globalThis.window.localStorage.removeItem("mebi-auth-store");
      globalThis.window.localStorage.removeItem("mebi-current-role");
      globalThis.window.localStorage.removeItem("mebi-persisted-role");

    }
  },

  /**
   * Set remember me preference
   */
  async setRememberMe(remember: boolean): Promise<void> {
    await Preferences.set({
      key: STORAGE_KEYS.REMEMBER_ME,
      value: JSON.stringify(remember),
    });
  },

  /**
   * Get remember me preference
   */
  async getRememberMe(): Promise<boolean> {
    const { value } = await Preferences.get({ key: STORAGE_KEYS.REMEMBER_ME });
    return value ? JSON.parse(value) : false;
  },

  /**
   * Save account for quick login
   */
  async saveAccount(account: SavedAccount): Promise<void> {
    // Enforce SINGLE saved account policy as per user requirement (`User confirmed only 1 account is saved at a time`)
    // We overwrite the entire array with the new account
    const newAccounts = [account];

    await Preferences.set({
      key: STORAGE_KEYS.SAVED_ACCOUNTS,
      value: JSON.stringify(newAccounts),
    });
  },

  /**
   * Get saved accounts
   */
  async getSavedAccounts(): Promise<SavedAccount[]> {
    const { value } = await Preferences.get({
      key: STORAGE_KEYS.SAVED_ACCOUNTS,
    });
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      // Handle legacy structure { accounts: [], selectedAccountEmail: ... }
      if (!Array.isArray(parsed) && parsed.accounts) {
        return parsed.accounts;
      }
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },

  /**
   * Get saved accounts with selection info
   */
  async getSavedAccountsWithSelection(): Promise<{
    accounts: SavedAccount[];
    selectedAccountEmail: string | null;
  }> {
    const accounts = await this.getSavedAccounts();
    const { value } = await Preferences.get({
      key: STORAGE_KEYS.SELECTED_ACCOUNT,
    });

    // Also check legacy storage if selected account is missing
    let selected = value;
    if (!selected) {
      const raw = await Preferences.get({ key: STORAGE_KEYS.SAVED_ACCOUNTS });
      if (raw.value) {
        try {
          const parsed = JSON.parse(raw.value);
          if (!Array.isArray(parsed) && parsed.selectedAccountEmail) {
            selected = parsed.selectedAccountEmail;
          }
        } catch {
          // Ignore legacy parsing error
        }
      }
    }

    return { accounts, selectedAccountEmail: selected };
  },

  /**
   * Remove saved account
   */
  async removeSavedAccount(email: string): Promise<void> {
    let { accounts, selectedAccountEmail } =
      await this.getSavedAccountsWithSelection();
    accounts = accounts.filter((a) => a.email !== email);

    if (selectedAccountEmail === email) {
      selectedAccountEmail = null;
      await Preferences.remove({ key: STORAGE_KEYS.SELECTED_ACCOUNT });
    }

    await Preferences.set({
      key: STORAGE_KEYS.SAVED_ACCOUNTS,
      value: JSON.stringify(accounts),
    });
  },

  /**
   * Set selected account
   */
  async setSelectedAccount(email: string): Promise<void> {
    await Preferences.set({ key: STORAGE_KEYS.SELECTED_ACCOUNT, value: email });
  },

  /**
   * Clear all saved accounts
   */
  async clearSavedAccounts(): Promise<void> {
    await Preferences.remove({ key: STORAGE_KEYS.SAVED_ACCOUNTS });
    await Preferences.remove({ key: STORAGE_KEYS.SELECTED_ACCOUNT });
  },

  /**
   * Clear all session data
   */
  async clearAll(): Promise<void> {
    await this.clearTokens();
    await Preferences.remove({ key: STORAGE_KEYS.REMEMBER_ME });
    await Preferences.remove({ key: STORAGE_KEYS.SAVED_ACCOUNTS });
    await Preferences.remove({ key: STORAGE_KEYS.SELECTED_ACCOUNT });
  },
};
